import { SidebarMenuButton, cn } from 'shared-ui';
import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { OrbitIconWrapper } from '@/components/app/OrbitIconWrapper.tsx';

export type OrbitSidebarMenuButtonProps = React.ComponentProps<typeof SidebarMenuButton>;

export function OrbitSidebarMenuButton({ children, isActive, className, ...props }: OrbitSidebarMenuButtonProps) {
    return (
        <>
            {isActive && (
                /*
                 * Active accent tab — positioned absolute relative to SidebarMenuItem
                 * (the nearest `position: relative` ancestor, which is the <li>).
                 * Rendered as a sibling so it escapes the button's overflow-hidden clipping
                 * and sits flush with the sidebar's far-left edge.
                 */
                <span
                    aria-hidden="true"
                    className="bg-origin-blue absolute inset-y-0 -left-2 z-10 my-auto h-10 w-1.5 rounded-r-full"
                />
            )}
            <SidebarMenuButton
                size="lg"
                isActive={isActive}
                className={cn(
                    'cursor-pointer',
                    // Blue stroke belongs on keyboard focus only, never on hover.
                    'focus-visible:ring-origin-blue/50 focus-visible:ring-3',
                    'hover:text-origin-blue active:bg-transparent',
                    // Stop the base transition-[width,height,padding] from animating the button during collapse
                    'transition-none!',
                    'group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:size-12! group-data-[collapsible=icon]:max-w-12! group-data-[collapsible=icon]:min-w-12! group-data-[collapsible=icon]:items-center! group-data-[collapsible=icon]:justify-center! group-data-[collapsible=icon]:gap-0! group-data-[collapsible=icon]:overflow-hidden! group-data-[collapsible=icon]:p-0!',
                    // Hide direct span children (labels) in collapsed mode; icon wrapper is a div so it stays visible
                    'group-data-[collapsible=icon]:[&>span]:hidden',
                    // Override the default grey active fill; apply Origin Blue text instead
                    'data-active:text-origin-blue data-active:bg-transparent',
                    className,
                )}
                {...props}
            >
                {children}
            </SidebarMenuButton>
        </>
    );
}

export type OrbitSidebarMenuButtonIconProps = { icon: LucideIcon; imgSrc?: never } | { imgSrc: string; icon?: never };

OrbitSidebarMenuButton.Icon = ({ ...props }: OrbitSidebarMenuButtonIconProps) => {
    return <OrbitIconWrapper {...props} />;
};
